import { Button } from "azure-devops-ui/Button";
import { FocusZone, FocusZoneDirection } from "azure-devops-ui/FocusZone";
import { IReadonlyObservableValue } from "azure-devops-ui/Core/Observable";
import { css } from "azure-devops-ui/Util";
import React from "react";
import { Observer } from "../../../common/components/observer/observer";
import { EventContext } from "../../../common/contexts/event";
import { useFocusTracker } from "../../../common/hooks/usefocustracker";
import { useObservable, useSubscription } from "../../../common/hooks/useobservable";
import { useResize } from "../../../common/hooks/useresize";
import { ISelection, Selection } from "../../../common/utilities/selection";
import { SessionContext } from "../../contexts/session";
import { UserPreferencesContext } from "../../contexts/userpreferences";
import { useLayoutStyle } from "../../hooks/uselayoutstyle";
import { NavigationPanel } from "../../frame/navigation";
import { IAccount } from "../../types/account";
import { getActiveSearch } from "../../utilities/util";
import { ISearchForm, SearchForm } from "../search/searchform";

import "./pagecommandbar.css";

const { ToggleNavigation } = window.Resources.Common;
const { SearchPlaceholder2 } = window.Resources.SearchInput;

interface IPageCommandbarProps {
  accountDetails: IReadonlyObservableValue<IAccount | undefined>;
  children?: (selection: Set<string>) => React.ReactElement | null;
  className?: string;
  defaultElementIds?: { selectionActive: string; selectionInactive: string };
  raised?: boolean;
  selection?: ISelection;
}

export function PageCommandbar(props: IPageCommandbarProps): React.ReactElement {
  const { accountDetails, children, className, defaultElementIds, raised } = props;

  const eventContext = React.useContext(EventContext);
  const sessionContext = React.useContext(SessionContext);
  const userPreferencesContext = React.useContext(UserPreferencesContext);

  const rootElement = React.useRef<HTMLDivElement>(null);
  const searchForm = React.useRef<ISearchForm>();

  const [localSelection] = React.useState(() => new Selection());
  const [searchActive, setSearchActive] = useObservable(false);
  const [showNavigation, setShowNavigation] = useObservable(false);

  // Subscribe to changes in the selection and re-render the commandbar.
  const selection = useSubscription(props.selection || localSelection);

  const layoutStyle = useLayoutStyle(
    React.useCallback(
      (layoutStyle) => {
        // Hide the navigation if we expand and are no longer in mobile mode and
        // the navigation is currently visible.
        if (layoutStyle === "desktop" && showNavigation.value) {
          setShowNavigation(false);
        }
      },
      [setShowNavigation, showNavigation.value]
    )
  );

  const { onBlur, onFocus } = useFocusTracker({ onFocusChange: setSearchActive });

  useResize(
    rootElement,
    React.useCallback(() => searchForm.current?.updateLayout(), [])
  );

  const desktopLayout = layoutStyle === "desktop";
  const selectedCount = selection.size;
  const raisedCommandBar = selectedCount > 0 || raised;

  const defaultElementId =
    !desktopLayout && sessionContext.authenticated() && !sessionContext.embedded
      ? "open-navigation"
      : defaultElementIds
      ? selection.size
        ? defaultElementIds.selectionActive
        : defaultElementIds.selectionInactive
      : undefined;

  const photoPreferences = useSubscription(userPreferencesContext.getUserPreferences("photo"));

  return (
    <div className="relative flex-row flex-noshrink" ref={rootElement}>
      <FocusZone direction={FocusZoneDirection.Horizontal} focusGroupProps={{ defaultElementId }} handleTabKey={false}>
        <div
          className={css(
            "page-command-bar command-bar",
            className,
            "flex-row flex-grow flex-align-center padding-vertical-8 overflow-hidden",
            raisedCommandBar && "page-command-bar-raised",
            !desktopLayout && raisedCommandBar && "depth-16",
            desktopLayout && selectedCount === 0 && !raised && "margin-horizontal-32",
            desktopLayout && raisedCommandBar && "margin-horizontal-24 padding-horizontal-8 depth-16"
          )}
          role="menubar"
        >
          {!desktopLayout && sessionContext.authenticated() && !sessionContext.embedded && (
            <div className={css("page-command-bar-inner flex-row flex-align-center margin-left-8")}>
              <Button
                ariaLabel={ToggleNavigation}
                className="toggle-navigation"
                iconProps={{ iconName: "GlobalNavButton" }}
                id="open-navigation"
                key="toggle-navigation"
                onClick={() => {
                  eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "showNavigationPanel" });
                  setShowNavigation(!showNavigation.value);
                }}
                role="menuitem"
                subtle={true}
              />
              <div className={css("separator-line-right margin-vertical-4 margin-left-8")} />
            </div>
          )}
          <div className={css("page-command-bar-inner flex-row flex-align-center flex-grow", !desktopLayout && "margin-horizontal-12")}>
            {children && children(selection)}
          </div>
        </div>
      </FocusZone>
      {photoPreferences?.florenceSearch && (
        <div className={css("search-bar-container absolute-fill flex-row flex-justify-center pointer-events-none", raisedCommandBar && "hidden")}>
          <Observer values={{ searchActive }}>
            {({ searchActive }) => (
              <div
                className={css(
                  "search-bar absolute flex-row flex-noshrink margin-horizontal-16 padding-vertical-4 depth-8 overflow-hidden pointer-events-auto",
                  searchActive && "active"
                )}
                onBlur={onBlur}
                onFocus={onFocus}
              >
                <SearchForm
                  activeSearch={getActiveSearch(sessionContext)}
                  componentRef={searchForm}
                  enableDropdown={true}
                  placeholder={SearchPlaceholder2}
                />
              </div>
            )}
          </Observer>
        </div>
      )}
      <Observer values={{ showNavigation }}>
        {({ showNavigation }) =>
          showNavigation ? <NavigationPanel accountDetails={accountDetails} onDismiss={() => setShowNavigation(false)} /> : null
        }
      </Observer>
    </div>
  );
}
